import { ReactNode, useEffect, useState } from 'react';

import { useIsMutating } from '@tanstack/react-query';

import { MessageBus } from '@app-velocity/message-bus';
import { useGetConfig } from '@app-velocity/config';

import { getCoreVersion } from '../core/methods';
import { useConfigureScenarioStore } from './store/useConfigureScenarioStore';

const messageBus = new MessageBus('system');

export type AppVersionControlProps = {
  children: ReactNode;
};

export const AppVersionControl = ({ children }: AppVersionControlProps) => {
  const isConfiguring = useConfigureScenarioStore(
    (store) => store.isConfiguring
  );

  const isMutating = useIsMutating();

  const { data: config } = useGetConfig();
  const [coreVersion, setCoreVersion] = useState('');

  const getBuildCoreVersion = async () => {
    const version = await getCoreVersion();
    setCoreVersion(version);
  };

  useEffect(() => {
    getBuildCoreVersion();
  }, []);

  useEffect(() => {
    if (!config) return;

    // if scenario is generating
    if (isConfiguring) return;

    // if there are pending mutations
    if (isMutating > 0) return;

    const { version: pluginVersion } = config;

    if (!pluginVersion || import.meta.env.VITE_ENV === 'local') return;

    const { uiVersion: receivedUiVersion, coreVersion: receivedCoreVersion } =
      pluginVersion;

    const allValidUIVersions = (receivedUiVersion || '').split('|');
    const allValidCoreVersions = (receivedCoreVersion || '').split('|');

    if (
      receivedUiVersion &&
      BUILD_VERSION &&
      BUILD_VERSION !== receivedCoreVersion &&
      !allValidUIVersions.includes(BUILD_VERSION)
    ) {
      messageBus.publish('modal:new-plugin-ui-version:open', {
        closable: false,
      });

      return;
    }

    if (
      receivedCoreVersion &&
      coreVersion &&
      coreVersion !== receivedCoreVersion &&
      !allValidCoreVersions.includes(coreVersion)
    ) {
      messageBus.publish('modal:new-plugin-version:open', { closable: false });
    }
  }, [config, coreVersion, isMutating, isConfiguring]);

  return <>{children}</>;
};
